import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { BoelsUser, AppConfig, UserEnum } from '@boels/models';
import {
  QaHooksDirective,
  TypedTemplateDirective,
  WindowRef,
  BreakpointService,
} from '@boels/helpers';
import { LoginComponent } from './login/login.component';
import { AccountIndicatorComponent } from './account-indicator/account-indicator.component';
import { TranslocoModule } from '@jsverse/transloco';
import {
  faFileContract,
  faFileInvoice,
  faGlobe,
  faRightFromBracket,
  faUser,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { ToggleLanguageSelectorComponent } from '../toggle-language-selector/toggle-language-selector.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AccountMobileComponent } from './account-mobile/account-mobile.component';

type MenuItem = {
  icon?: Readonly<IconDefinition>;
  link?: string;
  imgSrc?: string;
  title?: string;
  subtitle?: string;
  subtitleBinding?: 'debtor-name';
  showLanguageSelector?: boolean;
  logout?: boolean;
  shouldHaveDebtorData?: boolean;
  iconClass?: string;
};

const MENUTITEMS: MenuItem[] = [
  {
    title: 'header.auth.account',
    icon: faUser,
    link: 'account',
    subtitleBinding: 'debtor-name',
    iconClass: 'account-icon',
  },
  {
    title: 'header.auth.countryLanguage',
    icon: faGlobe,
    showLanguageSelector: true,
    iconClass: 'language-icon',
  },
  {
    subtitle: 'header.auth.product',
    link: 'products',
    imgSrc: 'assets/images/icons/my-products.svg',
    shouldHaveDebtorData: true,
    iconClass: 'products-icon',
  },
  {
    subtitle: 'header.auth.contract',
    link: 'contracts',
    icon: faFileContract,
    shouldHaveDebtorData: true,
    iconClass: 'contracts-icon',
  },
  {
    subtitle: 'header.auth.invoice',
    link: 'invoices',
    icon: faFileInvoice,
    shouldHaveDebtorData: true,
    iconClass: 'invoices-icon',
  },
  {
    subtitle: 'header.auth.logout',
    icon: faRightFromBracket,
    logout: true,
    iconClass: 'logout-icon',
  },
];

@Component({
  selector: 'boels-account',
  imports: [
    TranslocoModule,
    LoginComponent,
    AccountIndicatorComponent,
    TypedTemplateDirective,
    ToggleLanguageSelectorComponent,
    FaIconComponent,
    NgTemplateOutlet,
    QaHooksDirective,
    NgClass,
    AccountMobileComponent,
  ],
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class AccountComponent {
  private environment = inject<AppConfig>('ENVIRONMENT' as any);

  #winRef = inject(WindowRef);
  #breakpointService = inject(BreakpointService);

  @ViewChild('accountMenu') accountMenu?: ElementRef;
  @ViewChild('accountButton') accountButton?: ElementRef;
  @ViewChildren('menuItem') menuItemElements?: QueryList<ElementRef>;

  boelsUser = input.required<BoelsUser>();
  activeCulture = input.required<string | undefined>();

  $isNavigationOverlayOpen = signal<boolean>(false);
  $isMobileView = signal<boolean>(false);

  login = output<void>();
  logout = output<void>();
  openLanguageSelector = output<void>();

  protected readonly userType = UserEnum;
  protected readonly menuItemsList: MenuItem[] = MENUTITEMS;

  constructor() {
    this.#breakpointService.mdAndBelow$.subscribe((isMobile) => {
      this.$isMobileView.set(isMobile);
      if (isMobile && this.$isNavigationOverlayOpen()) {
        this.setAccountNavigationOverlay(false);
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.$isNavigationOverlayOpen()) {
      const targetElement = event.target as HTMLElement;
      const accountButton = this.accountButton?.nativeElement;
      const menuElement = this.accountMenu?.nativeElement;

      // Check if click is outside both the button and the menu
      if (
        accountButton &&
        menuElement &&
        !accountButton.contains(targetElement) &&
        !menuElement.contains(targetElement)
      ) {
        this.setAccountNavigationOverlay(false);
      }
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.$isNavigationOverlayOpen() || this.$isMobileView()) return;

    switch (event.key) {
      case 'Escape':
        this.setAccountNavigationOverlay(false);
        this.accountButton?.nativeElement?.focus();
        break;
      case 'Tab': {
        if (!this.menuItemElements?.length) return;

        const focusableElements = this.menuItemElements.toArray();
        const firstElement = focusableElements[0]?.nativeElement;
        const lastElement =
          focusableElements[focusableElements.length - 1]?.nativeElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
        break;
      }
    }
  }

  handleAccountButtonKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleAccountNavigationOverlay(event, true);
    }
  }

  toggleAccountNavigationOverlay(event: Event, fromKeyboard = false): void {
    event.stopPropagation();

    // On mobile, non-authenticated users should be directed to login
    if (
      this.$isMobileView() &&
      this.boelsUser().type !== UserEnum.B2C_AUTHENTICATED
    ) {
      this.login.emit();
      return;
    }

    // For all other cases (desktop or mobile authenticated), toggle the menu
    const newState = !this.$isNavigationOverlayOpen();
    this.setAccountNavigationOverlay(newState);

    if (newState && !this.$isMobileView() && fromKeyboard) {
      requestAnimationFrame(() => {
        this.menuItemElements?.first?.nativeElement?.focus();
      });
    }
  }

  logoutClicked(): void {
    this.logout.emit();
  }

  menuItemClicked(menuItem: MenuItem, event: Event): void {
    event.stopPropagation();
    this.setAccountNavigationOverlay(false);

    if (menuItem.link) {
      this.openMyBoelsUrl(menuItem.link);
    } else if (menuItem.logout) {
      this.logoutClicked();
    } else if (menuItem.showLanguageSelector) {
      this.openLanguageSelector.emit();
    }
  }

  handleMenuItemKeyDown(menuItem: MenuItem, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.menuItemClicked(menuItem, event);
    }
  }

  openMyBoelsUrl(routeSlug: string) {
    let culture = this.activeCulture();
    if (culture === 'hu-hu') {
      culture = 'en-hu';
    }

    this.#winRef.nativeWindow?.open(
      this.environment.myBoelsUrl + '/' + routeSlug + '?locale=' + culture,
      '_blank',
    );
  }

  setAccountNavigationOverlay(value: boolean): void {
    this.$isNavigationOverlayOpen.set(value);
  }

  getSubtitle(subtitleBinding: string): string {
    switch (subtitleBinding) {
      case 'debtor-name':
        return this.boelsUser().debtor?.name as string;
    }
    return '';
  }
}
